import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import { useEffect, useState, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { RemoveFormFieldButton } from '@/components/Buttons/RemoveFormFieldButton/RemoveFormFieldButton';
import { BasicChip } from '@/components/Chips/BasicChip';
import { FormAutocompleteField } from '@/components/Form/FormAutocompleteField/FormAutocompleteField';
import { createPhoneGroupHandlers } from '@/components/Form/FormPhoneNumberField/createPhoneGroupHandlers';
import { FormPhoneNumberField } from '@/components/Form/FormPhoneNumberField/FormPhoneNumberField';
import { FormTextField } from '@/components/Form/FormTextField/FormTextField';
import { Address } from '@/core/graphql/generated/types';
import { useDcrFields } from '@/features/DataChangeRequest/_constants/useDcrFields';
import { useDcrContext } from '@/features/DataChangeRequest/_context/DcrContext';
import { DcrFormController } from '@/features/DataChangeRequest/DcrFormController/DcrFormController';
import { formatAddressTitle } from '@/utils/addresses/formatAddressTitle';
import type { RequirePair } from '@/utils/typescript/RequirePair';

import { useDirtyFieldCheck } from '../../../_hooks/useDirtyFieldCheck';
import type { AddressSectionValuesIn } from '../../../_schemas/AddressSection.validation';

import { addressSectionCardStyles as sx } from './AddressSectionCard.styles';

type AddressData = AddressSectionValuesIn['addresses'][number];

const POTENTIAL_DIRTY_FIELDS: (keyof AddressData)[] = [
	'streetAddress1Cda_V',
	'streetAddress2Cda_V',
	'cityCda_V',
	'stateCda_V',
	'postalCodeCda_V',
	'postalCodeFullUs_V',
	'countryCda_V',
	'subStateAdminUnit_V',
	'primaryCda_V',
	'phoneCda_V',
	'phoneCountryIso',
	'statusCda_V',
	'faxCda_V',
	'faxCountryIso'
	// Note(Filip): As of 27.01.2026 - Jonas reqested these to be hidden for the time being
	// 'cbsa_V',
];

type Props = {
	index: number;
	onSetPrimary: () => void;
	isLastItem: boolean;
	isExpanded: boolean;
	originalData?: AddressData;
	isNewCard: boolean;
} & RequirePair<'canBeRemoved', 'onRemove', () => void>;

export const AddressSectionCard = ({
	index,
	onRemove,
	// onSetPrimary,
	canBeRemoved,
	isLastItem,
	isExpanded,
	originalData,
	isNewCard
}: Props) => {
	const { t } = useTranslation();
	const { addressFields } = useDcrFields();
	const {
		control,
		setValue,
		getValues,
		formState: { errors }
	} = useFormContext<AddressSectionValuesIn>();

	const [expanded, setExpanded] = useState(isExpanded);
	const {
		picklists: { countryOptions, stateOptionsDictionary },
		entityCountryId
	} = useDcrContext();
	const section = 'addresses';

	useEffect(() => {
		setExpanded(isExpanded);
	}, [isExpanded]);

	const values = useWatch({
		control,
		name: `addresses.${index}`
	});

	const currentCountryValue = values?.countryCda_V;

	const currentCountryOption = useMemo(
		() =>
			countryOptions?.find(
				c => c.id === currentCountryValue || c.name === currentCountryValue
			),
		[countryOptions, currentCountryValue]
	);

	const currentCountryId = currentCountryOption?.id;

	const stateOptions = useMemo(() => {
		if (!currentCountryId || !stateOptionsDictionary) return [];
		return stateOptionsDictionary[currentCountryId] || [];
	}, [currentCountryId, stateOptionsDictionary]);

	const headerTitle = formatAddressTitle(values as Address) || t('New Address');

	const checkIsDirty = useDirtyFieldCheck<AddressData>(
		isNewCard,
		originalData,
		['primaryCda_V']
	);

	const hasAnyFieldDirty = useMemo(() => {
		if (!values) return false;

		return POTENTIAL_DIRTY_FIELDS.some(key => {
			const isFieldDirty = checkIsDirty(key, values[key]);
			return isFieldDirty;
		});
	}, [values, checkIsDirty]);

	const handleCountryChange = (newCountryName: string) => {
		if (newCountryName !== values.countryCda_V) {
			setValue(`addresses.${index}.stateCda_V`, '', { shouldDirty: true });
		}
	};

	const hasError = useMemo(() => {
		return !!errors.addresses?.[index];
	}, [errors.addresses, index]);

	return (
		<Box sx={sx.root}>
			<Box
				sx={() => sx.header(isLastItem)}
				onClick={() => setExpanded(!expanded)}
			>
				<Box sx={sx.headerContent(hasAnyFieldDirty, hasError)}>
					<ExpandMoreIcon sx={sx.expandIcon(expanded)} />
					<Typography dir='auto' sx={sx.headerText}>
						{headerTitle}
					</Typography>

					<Box sx={sx.primaryToggleWrap}>
						<DcrFormController
							name={`addresses.${index}.primaryCda_V`}
							control={control}
							filterable={false}
							render={({ field }) =>
								!field.value ? (
									<></>
								) : (
									<BasicChip label={t('Primary')} variant='primary' />
								)
							}
						/>
					</Box>
				</Box>

				{canBeRemoved && (
					<Box sx={sx.removeButton}>
						<RemoveFormFieldButton type='button' onClick={onRemove} />
					</Box>
				)}

				<DcrFormController
					name={`addresses.${index}.statusCda_V`}
					control={control}
					filterable={false}
					render={({ field }) => (
						<FormControlLabel
							label={t('Active')}
							sx={{
								marginRight: 0.5
							}}
							control={
								<Switch
									checked={field.value === 'Active'}
									onClick={e => e.stopPropagation()}
									onChange={(_, checked) => {
										const newValue = checked ? 'Active' : 'Inactive';
										field.onChange(newValue);
									}}
								/>
							}
						/>
					)}
				/>
			</Box>

			<Collapse in={expanded}>
				<Box sx={sx.content(expanded)}>
					<Box sx={sx.gridContainer}>
						{/* Street 1 */}
						<DcrFormController
							name={`addresses.${index}.streetAddress1Cda_V`}
							section={section}
							control={control}
							render={({ field, fieldState }) => (
								<FormTextField
									id={field.name}
									name={field.name}
									isRequired
									label={addressFields.streetAddress1Cda_V.label}
									defaultValue={(field.value as string) ?? ''}
									onChange={field.onChange}
									indicateError={!!fieldState.error}
									isDirty={checkIsDirty('streetAddress1Cda_V', field.value)}
								/>
							)}
						/>
						{/* Note (Filip): As requested by Jonas on 23.01.2026, the logic for setting a primary entity should stay, but we are currently displaying a "Primary" chip */}
						{/* <Box sx={sx.primaryToggleWrap}>
								<DcrFormController
									name={`addresses.${index}.primaryCda_V`}
							section={section}
									control={control}
									render={({ field, fieldState }) => (
										<LabeledRadio
											label={t('Primary')}
											disabled={true} // As of 22.01.2026 - request by Jonas
											checked={!!field.value}
											onChange={onSetPrimary}
											indicateError={!!fieldState.error}
											isDirty={checkIsDirty('primaryCda_V', field.value)}
										/>
									)}
								/>
							</Box> */}
						{/* Street 2 */}
						<DcrFormController
							name={`addresses.${index}.streetAddress2Cda_V`}
							section={section}
							control={control}
							render={({ field, fieldState }) => (
								<FormTextField
									id={field.name}
									name={field.name}
									label={addressFields.streetAddress2Cda_V.label}
									defaultValue={(field.value as string) ?? ''}
									onChange={field.onChange}
									indicateError={!!fieldState.error}
									isDirty={checkIsDirty('streetAddress2Cda_V', field.value)}
								/>
							)}
						/>
						{/* City */}
						<DcrFormController
							name={`addresses.${index}.cityCda_V`}
							section={section}
							control={control}
							render={({ field, fieldState }) => (
								<FormTextField
									id={field.name}
									name={field.name}
									label={addressFields.cityCda_V.label}
									isRequired
									defaultValue={(field.value as string) ?? ''}
									onChange={field.onChange}
									indicateError={!!fieldState.error}
									isDirty={checkIsDirty('cityCda_V', field.value)}
								/>
							)}
						/>
						{/* State */}
						<DcrFormController
							name={`addresses.${index}.stateCda_V`}
							section={section}
							control={control}
							render={({ field, fieldState }) => (
								<FormAutocompleteField
									id={field.name}
									name={field.name}
									section={section}
									label={addressFields.stateCda_V.label}
									options={stateOptions}
									handleOnDropdownItemSelect={item => {
										const option = stateOptions.find(o => o.id === item?.id);
										field.onChange(option?.name ?? item?.id ?? '');
									}}
									defaultValue={(field.value as string) ?? ''}
									canBeRemoved={false}
									canAddNewItem={false}
									indicateError={!!fieldState.error}
									isDirty={checkIsDirty('stateCda_V', field.value)}
									disabled={!currentCountryId || stateOptions.length === 0}
								/>
							)}
						/>
						{/* Postal Code */}
						<DcrFormController
							name={`addresses.${index}.postalCodeCda_V`}
							section={section}
							control={control}
							render={({ field, fieldState }) => (
								<FormTextField
									id={field.name}
									name={field.name}
									label={addressFields.postalCodeCda_V.label}
									defaultValue={(field.value as string) ?? ''}
									onChange={field.onChange}
									indicateError={!!fieldState.error}
									isDirty={checkIsDirty('postalCodeCda_V', field.value)}
								/>
							)}
						/>
						{entityCountryId === 'us' && (
							<DcrFormController
								name={`addresses.${index}.postalCodeFullUs_V`}
								section={section}
								control={control}
								render={({ field, fieldState }) => (
									<FormTextField
										id={field.name}
										name={field.name}
										label={addressFields.postalCodeFullUs_V.label}
										defaultValue={(field.value as string) ?? ''}
										onChange={field.onChange}
										indicateError={!!fieldState.error}
										isDirty={checkIsDirty('postalCodeFullUs_V', field.value)}
									/>
								)}
							/>
						)}
						{/* Country */}
						<DcrFormController
							name={`addresses.${index}.countryCda_V`}
							control={control}
							section={section}
							render={({ field, fieldState }) => (
								<FormAutocompleteField
									id={field.name}
									name={field.name}
									section={section}
									label={addressFields.countryCda_V.label}
									options={countryOptions || []}
									handleOnDropdownItemSelect={item => {
										const option = countryOptions?.find(o => o.id === item?.id);
										const name = option?.name || item?.id;
										handleCountryChange(item?.name || '');
										field.onChange(name);
									}}
									defaultValue={(field.value as string) ?? ''}
									canBeRemoved={false}
									canAddNewItem={false}
									indicateError={!!fieldState.error}
									isDirty={checkIsDirty('countryCda_V', field.value)}
									isReadOnly
								/>
							)}
						/>
						{/* County */}
						<Box sx={sx.fullWidth}>
							<DcrFormController
								name={`addresses.${index}.subStateAdminUnit_V`}
								control={control}
								section={section}
								render={({ field, fieldState }) => (
									<FormTextField
										id={field.name}
										name={field.name}
										label={addressFields.subStateAdminUnit_V.label}
										defaultValue={(field.value as string) ?? ''}
										onChange={field.onChange}
										indicateError={!!fieldState.error}
										isDirty={checkIsDirty('subStateAdminUnit_V', field.value)}
									/>
								)}
							/>
						</Box>
						{/* Note(Filip): Temporary disabled - request by Jonas */}
						{/* CBSA */}
						{/* <DcrFormController
							name={`addresses.${index}.cbsa_V`}
							section={section}
							control={control}
							render={({ field, fieldState }) => (
								<FormAutocompleteField
									id={field.name}
									name={field.name}
									label={addressFields.cbsa_V.label}
									options={cbsaOptions}
									handleOnDropdownItemSelect={item => {
										const option = cbsaOptions.find(o => o.id === item?.id);
										const name = option?.name || item?.id;
										handleCountryChange(item?.name || '');
										field.onChange(name);
									}}
									defaultValue={(field.value as string) ?? ''}
									canBeRemoved={false}
									canAddNewItem={false}
									indicateError={!!fieldState.error}
									isDirty={checkIsDirty('cbsa_V', field.value)}
								/>
							)}
						/> */}
						{/* Phone + Country Code */}
						<DcrFormController
							name={`addresses.${index}.phoneCda_V`}
							section={section}
							control={control}
							render={({ field: phoneField, fieldState: phoneFieldState }) => (
								<DcrFormController
									name={`addresses.${index}.phoneCountryIso`}
									section={section}
									control={control}
									render={({ field: countryField }) => (
										<FormPhoneNumberField
											id={phoneField.name}
											name={phoneField.name}
											label={addressFields.phoneCda_V.label}
											countryIso={(countryField.value as string) ?? ''}
											phoneValue={(phoneField.value as string) ?? ''}
											{...createPhoneGroupHandlers(
												setValue,
												getValues,
												{
													name: `addresses.${index}.phoneCda_V`,
													value: (phoneField.value as string) ?? '',
													onChange: phoneField.onChange
												},
												{
													name: `addresses.${index}.phoneCountryIso`,
													value: (countryField.value as string) ?? '',
													onChange: countryField.onChange
												}
											)}
											errorMessage={phoneFieldState.error?.message}
											indicateError={!!phoneFieldState.error}
											isDirty={checkIsDirty('phoneCda_V', phoneField.value)}
										/>
									)}
								/>
							)}
						/>
						{/* Fax + Country Code */}
						<DcrFormController
							name={`addresses.${index}.faxCda_V`}
							section={section}
							control={control}
							render={({ field: faxField, fieldState: faxFieldState }) => (
								<DcrFormController
									name={`addresses.${index}.faxCountryIso`}
									section={section}
									control={control}
									render={({ field: faxCountryField }) => (
										<FormPhoneNumberField
											id={faxField.name}
											name={faxField.name}
											label={addressFields.faxCda_V.label}
											countryIso={(faxCountryField.value as string) ?? ''}
											phoneValue={(faxField.value as string) ?? ''}
											{...createPhoneGroupHandlers(
												setValue,
												getValues,
												{
													name: `addresses.${index}.faxCda_V`,
													value: (faxField.value as string) ?? '',
													onChange: faxField.onChange
												},
												{
													name: `addresses.${index}.faxCountryIso`,
													value: (faxCountryField.value as string) ?? '',
													onChange: faxCountryField.onChange
												}
											)}
											errorMessage={faxFieldState.error?.message}
											indicateError={!!faxFieldState.error}
											isDirty={checkIsDirty('faxCda_V', faxField.value)}
										/>
									)}
								/>
							)}
						/>
					</Box>
				</Box>
			</Collapse>
		</Box>
	);
};
